import { useRef, useState } from "react";
import { Check, Download, LoaderCircle, Sparkles, UploadCloud } from "lucide-react";
import { api } from "../lib/api";
import { uploadFile } from "../lib/upload";
import { renderCreative, type CreativeFormat, type HiggsfieldAsset } from "./creativeGen";

type Tile = {
  key: number;
  status: "queue" | "ready" | "uploading" | "linked" | "error";
  dataUrl?: string;
  blob?: Blob;
  asset?: HiggsfieldAsset;
  whopFileId?: string;
  message?: string;
};

// Compact generator rail docked inside the ad-cabinet dialog. Every creative
// gets a Whop file id the moment it is generated and streams straight into
// the builder's creative library via externalMediaIds.
export function HiggsfieldStudio({ onUploaded }: { onUploaded?: (fileId: string) => void }) {
  const [prompt, setPrompt] = useState("");
  const [format, setFormat] = useState<CreativeFormat>("1:1");
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const batchRef = useRef(0);
  const patchTile = (key: number, patch: Partial<Tile>) => {
    setTiles((current) => current.map((tile) => (tile.key === key ? { ...tile, ...patch } : tile)));
  };

  const generate = async () => {
    setGenerating(true);
    setError("");
    const batch = (batchRef.current += 1) * 1_000;
    const fresh: Tile[] = Array.from({ length: 4 }, (_, index) => ({ key: batch + index, status: "queue" }));
    setTiles(fresh);
    for (const [index, tile] of fresh.entries()) {
      try {
        await new Promise((resolve) => window.setTimeout(resolve, 350 + index * 420));
        const { blob, dataUrl } = await renderCreative(prompt, format, batch % 100_000 + index);
        const filename = `hf-creative-${crypto.randomUUID().slice(0, 8)}-${index + 1}.png`;
        const { asset } = await api<{ asset: HiggsfieldAsset }>("/api/ads/higgsfield-assets", {
          method: "POST",
          body: JSON.stringify({ prompt, format, filename }),
        });
        patchTile(tile.key, { status: "uploading", dataUrl, blob, asset });
        await addToLibrary({ ...tile, dataUrl, blob, asset });
      } catch (caught) {
        patchTile(tile.key, { status: "error", message: caught instanceof Error ? caught.message : "Generation failed." });
      }
    }
    setGenerating(false);
  };

  const addToLibrary = async (tile: Tile) => {
    if (!tile.blob || !tile.asset) return;
    patchTile(tile.key, { status: "uploading" });
    setError("");
    try {
      const file = new File([tile.blob], tile.asset.filename, { type: "image/png" });
      const uploaded = await uploadFile(file, "creative");
      const whopFileId = String(uploaded.id || "");
      await api("/api/ads/higgsfield-assets/link", {
        method: "POST",
        body: JSON.stringify({ asset_id: tile.asset.id, whop_file_id: whopFileId }),
      });
      patchTile(tile.key, { status: "linked", whopFileId });
      onUploaded?.(whopFileId);
    } catch (caught) {
      patchTile(tile.key, { status: "ready", message: caught instanceof Error ? caught.message : "The upload failed." });
      setError(caught instanceof Error ? caught.message : "The upload failed.");
    }
  };

  return (
    <aside className="hf-studio" aria-label="Higgsfield creative studio">
      <header className="hf-studio__head">
        <span className="hf-studio__badge"><Sparkles /> HIGGSFIELD</span>
        <h2>Building your<br /><em>campaign creatives</em></h2>
        <p>Generate creatives here. Each one lands in the builder’s creative library the moment it’s ready. Pick them From library in the media step.</p>
      </header>
      <label className="hf-studio__prompt">
        <span>What are you promoting?</span>
        <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={2} placeholder="Streetwear drop for Gen Z sneakerheads" />
      </label>
      <div className="hf-studio__formats" role="radiogroup" aria-label="Creative format">
        {(["1:1", "9:16", "16:9"] as const).map((option) => (
          <button key={option} role="radio" aria-checked={format === option} className={format === option ? "is-active" : ""} type="button" onClick={() => setFormat(option)}>{option}</button>
        ))}
      </div>
      <button className="button button--primary hf-studio__generate" type="button" disabled={generating} onClick={() => void generate()}>
        {generating ? <><LoaderCircle className="spin" /> Generating…</> : tiles.length ? "Regenerate creatives" : "Generate my creatives"}
      </button>
      {error && <p className="hf-studio__error" role="alert">{error}</p>}
      {tiles.length > 0 && <div className={`hf-studio__grid hf-studio__grid--${format.replace(":", "x")}`}>
        {tiles.map((tile) => (
          <figure
            key={tile.key}
            className={`hf-tile is-${tile.status}`}
          >
            {tile.status === "queue" && <span className="hf-tile__state"><LoaderCircle className="spin" /> In queue</span>}
            {tile.dataUrl && <img src={tile.dataUrl} alt="Generated creative" />}
            {tile.status === "error" && <span className="hf-tile__state">{tile.message}</span>}
            {(tile.status === "ready" || tile.status === "uploading" || tile.status === "linked") && tile.asset && (
              <figcaption>
                {tile.status !== "linked" && tile.message && <small className="hf-tile__note">{tile.message}</small>}
                {tile.status === "linked" && tile.whopFileId
                  ? <span className="hf-tile__linked"><Check /> In your creative library</span>
                  : <span className="hf-tile__actions">
                      <button type="button" disabled={tile.status === "uploading"} onClick={() => void addToLibrary(tile)}>{tile.status === "uploading" ? <LoaderCircle className="spin" /> : <UploadCloud />}{tile.status === "uploading" ? "Adding to library…" : "Retry upload"}</button>
                      <a href={tile.dataUrl} download={tile.asset.filename} aria-label="Download creative"><Download /></a>
                    </span>}
              </figcaption>
            )}
          </figure>
        ))}
      </div>}
    </aside>
  );
}
